import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const CARDS_PER_GROUP = 5;

const fetchRecentCards = async (categoryId) => {
  try {
    // Primero obtenemos las 20 tarjetas más recientes
    const { data: recentCards, error: recentError } = await supabase
      .from("cards")
      .select("id")
      .order("addedtime", { ascending: false })
      .limit(20);

    if (recentError) throw recentError;
    const recentCardIds = recentCards.map(card => card.id);

    // Si no es "All" (id: 9), filtramos por categoría específica
    let query = supabase
      .from("cards")
      .select(`
        id,
        companyname,
        addedtime,
        category_id
      `)
      .in('id', recentCardIds)
      .order('addedtime', { ascending: false });

    if (categoryId !== 9) {
      query = query.eq('category_id', categoryId);
    }

    const { data: categoryCards, error: categoryError } = await query;
    if (categoryError) throw categoryError;

    return categoryCards;
  } catch (error) {
    throw error;
  }
};

export function useJustInSubCategories({
  componentSize = "Desktop 1440",
  selectedCategoryId = 9
}) {
  const { handleError } = useQueryError();

  const { data: cards = [], isLoading, error } = useQuery({
    queryKey: queryKeys.cards.justIn(selectedCategoryId),
    queryFn: () => fetchRecentCards(selectedCategoryId),
    onError: handleError,
    staleTime: 1000 * 60 * 5, // 5 minutos
    cacheTime: 1000 * 60 * 30, // 30 minutos
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Procesar cards y organizar en grupos
  const cardGroups = React.useMemo(() => {
    const groups = [];
    for (let i = 0; i < cards.length; i += CARDS_PER_GROUP) {
      groups.push(cards.slice(i, Math.min(i + CARDS_PER_GROUP, cards.length)));
    }
    return groups;
  }, [cards]);

  // Obtener ancho del contenedor según tamaño
  const containerWidth = React.useMemo(() => {
    switch (componentSize) {
      case "Phone":
        return 362.4;
      case "Tablet":
        return 626;
      case "Desktop 1200":
        return 1057;
      case "Desktop 1440":
      default:
        return 1210;
    }
  }, [componentSize]);

  return {
    cardGroups,
    containerWidth,
    isLoading,
    error,
    isEmpty: cards.length === 0
  };
}